import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useLazyQuery } from '@apollo/client';
import { useAuth } from '../context/AuthContext';
import {
  MY_COUPONS_QUERY, CREATE_COUPON_MUTATION, DELETE_COUPON_MUTATION, REGISTER_AS_SELLER_MUTATION,
  SET_FLASH_OFFER_MUTATION, SELLER_DASHBOARD_QUERY, SELLER_CUSTOMER_LEDGER_QUERY,
  SELLER_PROFIT_LOSS_QUERY, SELLER_RATINGS_QUERY, UPDATE_ORDER_STATUS_MUTATION, SET_PRODUCT_COST_MUTATION,
  UPDATE_PROFILE_MUTATION,
} from '../graphql';
import { toCSV, downloadCSV, groupByPeriod } from '../utils/csv';
import { PERSONAS, PERSONA_GROUPS, personaLabel } from '../config/personas';
import UpiQrPicker from '../components/UpiQrPicker';

const money = (amt, cur) => `${(amt / 100).toFixed(2)} ${cur || ''}`.trim();
const STATUSES = ['pending', 'confirmed', 'shipped', 'delivered', 'cancelled', 'completed'];
const TABS = [
  { id: 'orders', label: '📦 Orders' },
  { id: 'customers', label: '👥 Customers' },
  { id: 'pnl', label: '📈 Profit / Loss' },
  { id: 'coupons', label: '🏷️ Coupons & Offers' },
  { id: 'ratings', label: '⭐ Ratings' },
];

function PersonaSelect({ value, onChange }) {
  return (
    <select className="input" value={value} onChange={e => onChange(e.target.value)}>
      <option value="">— Select persona (optional) —</option>
      {Object.entries(PERSONA_GROUPS).map(([g, label]) => (
        <optgroup key={g} label={label}>
          {PERSONAS.filter(p => p.group === g).map(p => <option key={p.id} value={p.id}>{p.emoji} {p.label}</option>)}
        </optgroup>
      ))}
    </select>
  );
}

// ── Seller self-registration (shown to non-sellers) ──
function SellerRegister() {
  const { updateCurrentUser } = useAuth();
  const [regForm, setRegForm] = useState({ storeName: '', storeDesc: '', persona: '', upiId: '' });
  const [regError, setRegError] = useState('');
  const [registerAsSeller, { loading }] = useMutation(REGISTER_AS_SELLER_MUTATION, {
    onCompleted: () => updateCurrentUser({ role: 'SELLER' }),
    onError: e => setRegError(e.message),
  });
  return (
    <div className="page">
      <div className="container" style={{ padding: '80px 16px', maxWidth: 560 }}>
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <div style={{ fontSize: 56, marginBottom: 12 }}>🛍️</div>
          <h1 style={{ fontSize: 'var(--font-xl)', fontWeight: 800, marginBottom: 8 }}>Become a Seller</h1>
          <p style={{ color: 'var(--text-muted)', lineHeight: 1.6 }}>Sell products, run offers of the day, track orders & customer accounts, and grow with ratings.</p>
        </div>
        <div className="card">
          <div className="form-group">
            <label className="form-label">Store / Brand name *</label>
            <input className="input" placeholder="e.g. Grehini Bakes" value={regForm.storeName} onChange={e => setRegForm(f => ({ ...f, storeName: e.target.value }))} />
          </div>
          <div className="form-group">
            <label className="form-label">Persona</label>
            <PersonaSelect value={regForm.persona} onChange={v => setRegForm(f => ({ ...f, persona: v }))} />
          </div>
          <div className="form-group">
            <label className="form-label">What do you sell / offer? (optional)</label>
            <textarea className="input" rows={2} placeholder="e.g. Home-baked cakes, pastries and gift hampers" value={regForm.storeDesc} onChange={e => setRegForm(f => ({ ...f, storeDesc: e.target.value }))} />
          </div>
          <div className="form-group">
            <label className="form-label">📲 UPI id (for receiving payments)</label>
            <input className="input" placeholder="e.g. yourname@okhdfcbank or 9876543210@ybl" value={regForm.upiId} onChange={e => setRegForm(f => ({ ...f, upiId: e.target.value }))} />
            <div style={{ fontSize: 11, color: 'var(--text-dim)', marginTop: 4 }}>Buyers pay you directly by UPI to this id. You can add it later too.</div>
          </div>
          {regError && <div style={{ color: 'var(--danger)', fontSize: 'var(--font-sm)', marginBottom: 10 }}>{regError}</div>}
          <button className="btn btn-primary" style={{ width: '100%' }} disabled={loading || !regForm.storeName.trim()}
            onClick={() => registerAsSeller({ variables: { storeName: regForm.storeName.trim(), description: regForm.storeDesc.trim() || null, persona: regForm.persona || null, upiId: regForm.upiId.trim() || null } })}>
            {loading ? 'Registering…' : "🛍️ Register as Seller — it's free!"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Orders tab ──
function OrdersTab({ orders, currency }) {
  const [updateStatus] = useMutation(UPDATE_ORDER_STATUS_MUTATION);
  const [busy, setBusy] = useState(null);
  const setStatus = async (orderId, status) => {
    setBusy(orderId);
    try { await updateStatus({ variables: { orderId, status } }); }
    catch (e) { alert(e.message); }
    finally { setBusy(null); }
  };
  const exportCSV = (period) => {
    if (period === 'orders') {
      downloadCSV(`seller-orders-${Date.now()}`, toCSV(orders.map(o => ({
        date: new Date(o.createdAt).toISOString().slice(0, 10), product: o.productName || o.product?.name,
        buyer: o.buyerName, qty: o.quantity, coins: o.totalCoins, amount: (o.totalAmount / 100).toFixed(2),
        currency: o.currency, method: o.paymentMethod, status: o.status, rating: o.rating || '',
      })), [
        { key: 'date', label: 'Date' }, { key: 'product', label: 'Product' }, { key: 'buyer', label: 'Buyer' },
        { key: 'qty', label: 'Qty' }, { key: 'coins', label: 'Coins' }, { key: 'amount', label: 'Amount' },
        { key: 'currency', label: 'Currency' }, { key: 'method', label: 'Method' }, { key: 'status', label: 'Status' }, { key: 'rating', label: 'Rating' },
      ]));
    } else {
      downloadCSV(`seller-orders-${period}-${Date.now()}`, toCSV(groupByPeriod(orders, period), [
        { key: 'period', label: period === 'month' ? 'Month' : period === 'week' ? 'Week' : 'Day' },
        { key: 'orders', label: 'Orders' }, { key: 'totalCoins', label: 'Total Coins' },
        { key: 'totalAmount', label: 'Total Amount', format: v => (v / 100).toFixed(2) },
      ]));
    }
  };
  return (
    <div className="card">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
        <h2 style={{ fontWeight: 800, fontSize: 'var(--font-lg)' }}>📦 Orders ({orders.length})</h2>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          <button className="btn btn-ghost btn-sm" onClick={() => exportCSV('orders')}>⬇ All</button>
          <button className="btn btn-ghost btn-sm" onClick={() => exportCSV('day')}>Day</button>
          <button className="btn btn-ghost btn-sm" onClick={() => exportCSV('week')}>Week</button>
          <button className="btn btn-ghost btn-sm" onClick={() => exportCSV('month')}>Month</button>
        </div>
      </div>
      {orders.length === 0 ? <p style={{ color: 'var(--text-muted)', fontSize: 'var(--font-sm)' }}>No orders yet.</p> : (
        <div style={{ display: 'grid', gap: 10 }}>
          {orders.map(o => (
            <div key={o.id} style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', padding: '10px 12px', background: 'var(--surface2)', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)' }}>
              {o.product?.imageUrl
                ? <img src={o.product.imageUrl} alt="" style={{ width: 32, height: 32, objectFit: 'cover', borderRadius: 6, flexShrink: 0 }} />
                : <span style={{ fontSize: 20 }}>{o.product?.imageEmoji || '📦'}</span>}
              <div style={{ flex: 1, minWidth: 150 }}>
                <div style={{ fontWeight: 700 }}>{o.productName || o.product?.name}</div>
                <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{o.buyerName} · {new Date(o.createdAt).toLocaleDateString()} · qty {o.quantity} · {o.totalCoins} coins {o.rating ? `· ★${o.rating}` : ''}</div>
              </div>
              <select className="input" style={{ width: 130, padding: '5px 8px' }} value={o.status} disabled={busy === o.id} onChange={e => setStatus(o.id, e.target.value)}>
                {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Customers tab (accounts + drill-down) ──
function CustomersTab({ customers, currency }) {
  const [open, setOpen] = useState(null);
  const [loadLedger, ledger] = useLazyQuery(SELLER_CUSTOMER_LEDGER_QUERY, { fetchPolicy: 'network-only' });
  const toggle = (id) => { if (open === id) { setOpen(null); return; } setOpen(id); loadLedger({ variables: { customerId: id } }); };
  const exportCSV = () => downloadCSV(`seller-customers-${Date.now()}`, toCSV(customers.map(c => ({
    name: c.name, city: c.city, orders: c.orderCount, coins: c.totalCoins, amount: (c.totalAmount / 100).toFixed(2),
    lastOrder: c.lastOrderAt ? new Date(c.lastOrderAt).toISOString().slice(0, 10) : '', avgRating: c.avgRating || '',
  })), [
    { key: 'name', label: 'Customer' }, { key: 'city', label: 'City' }, { key: 'orders', label: 'Orders' },
    { key: 'coins', label: 'Coins' }, { key: 'amount', label: 'Amount' }, { key: 'lastOrder', label: 'Last Order' }, { key: 'avgRating', label: 'Avg Rating' },
  ]));
  return (
    <div className="card">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <h2 style={{ fontWeight: 800, fontSize: 'var(--font-lg)' }}>👥 Catering to {customers.length} customer{customers.length === 1 ? '' : 's'}</h2>
        <button className="btn btn-ghost btn-sm" onClick={exportCSV}>⬇ CSV</button>
      </div>
      {customers.length === 0 ? <p style={{ color: 'var(--text-muted)', fontSize: 'var(--font-sm)' }}>No customers yet.</p> : customers.map(c => (
        <div key={c.userId} style={{ borderBottom: '1px solid var(--border)', padding: '10px 0' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }} onClick={() => toggle(c.userId)}>
            <div style={{ width: 34, height: 34, borderRadius: '50%', background: c.avatarColor, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700 }}>{c.name[0]}</div>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 700 }}>{c.name}</div>
              <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{c.orderCount} orders · {money(c.totalAmount, currency)} · {c.totalCoins} coins{c.avgRating != null ? ` · ★${c.avgRating}` : ''}</div>
            </div>
            <span style={{ color: 'var(--text-muted)' }}>{open === c.userId ? '▲' : '▼'}</span>
          </div>
          {open === c.userId && (
            <div style={{ paddingLeft: 44, marginTop: 6 }}>
              {ledger.loading ? <div className="spinner" /> : (ledger.data?.sellerCustomerLedger || []).map(o => (
                <div key={o.id} style={{ fontSize: 12, color: 'var(--text-muted)', padding: '3px 0' }}>
                  {new Date(o.createdAt).toLocaleDateString()} · {o.productName || o.product?.name} · {o.totalCoins} coins · <span style={{ textTransform: 'capitalize' }}>{o.status}</span>{o.rating ? ` · ★${o.rating}` : ''}
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// ── P&L tab ──
function PnLTab({ orders, currency }) {
  const [range, setRange] = useState({ from: '', to: '' });
  const { data, refetch } = useQuery(SELLER_PROFIT_LOSS_QUERY, { fetchPolicy: 'cache-and-network' });
  const [setCost] = useMutation(SET_PRODUCT_COST_MUTATION);
  const pnl = data?.sellerProfitLoss;
  // Distinct products seen in orders — lets the seller set unit cost for P&L.
  const products = useMemo(() => {
    const map = {};
    for (const o of orders) { if (o.product && !map[o.product.id]) map[o.product.id] = o.product; }
    return Object.values(map);
  }, [orders]);
  const [costs, setCosts] = useState({});
  const saveCost = async (id) => {
    const c = parseInt(costs[id]);
    if (isNaN(c)) return;
    try { await setCost({ variables: { productId: id, unitCost: c } }); refetch(); }
    catch (e) { alert(e.message); }
  };
  const apply = () => refetch({ from: range.from || null, to: range.to || null });
  return (
    <div>
      <div className="card" style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: 12 }}>
          <div className="form-group" style={{ margin: 0 }}><label className="form-label">From</label><input className="input" type="date" value={range.from} onChange={e => setRange(r => ({ ...r, from: e.target.value }))} /></div>
          <div className="form-group" style={{ margin: 0 }}><label className="form-label">To</label><input className="input" type="date" value={range.to} onChange={e => setRange(r => ({ ...r, to: e.target.value }))} /></div>
          <button className="btn btn-ghost btn-sm" onClick={apply}>Apply</button>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 12 }}>
          <div style={{ textAlign: 'center' }}><div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Revenue</div><div style={{ fontSize: 22, fontWeight: 800, color: 'var(--primary)' }}>{money(pnl?.revenue ?? 0, currency)}</div></div>
          <div style={{ textAlign: 'center' }}><div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Cost</div><div style={{ fontSize: 22, fontWeight: 800, color: 'var(--warning)' }}>{money(pnl?.cost ?? 0, currency)}</div></div>
          <div style={{ textAlign: 'center' }}><div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Profit</div><div style={{ fontSize: 22, fontWeight: 800, color: (pnl?.profit ?? 0) >= 0 ? 'var(--success)' : 'var(--danger)' }}>{money(pnl?.profit ?? 0, currency)}</div></div>
        </div>
      </div>
      <div className="card">
        <h3 style={{ fontWeight: 800, marginBottom: 4 }}>Set unit cost per product</h3>
        <p style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 12 }}>Cost is in smallest currency unit (pence/paise). Profit = revenue − (qty × unit cost).</p>
        {products.length === 0 ? <p style={{ color: 'var(--text-muted)', fontSize: 'var(--font-sm)' }}>Products with sales appear here.</p> : products.map(p => (
          <div key={p.id} style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '6px 0' }}>
            <span style={{ flex: 1 }}>{p.imageEmoji} {p.name} <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>(price {p.price} coins, cost {p.unitCost ?? 0})</span></span>
            <input className="input" style={{ width: 100, padding: '5px 8px' }} type="number" min={0} placeholder="cost" value={costs[p.id] ?? ''} onChange={e => setCosts(c => ({ ...c, [p.id]: e.target.value }))} />
            <button className="btn btn-ghost btn-sm" onClick={() => saveCost(p.id)}>Save</button>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Ratings tab ──
function RatingsTab({ sellerId }) {
  const { data, loading } = useQuery(SELLER_RATINGS_QUERY, { variables: { sellerId }, fetchPolicy: 'cache-and-network' });
  const r = data?.sellerRatings;
  return (
    <div className="card">
      <h2 style={{ fontWeight: 800, fontSize: 'var(--font-lg)', marginBottom: 12 }}>⭐ Your ratings {r?.avg != null && <span style={{ color: '#f59e0b' }}>{r.avg} ({r.count})</span>}</h2>
      {loading ? <div className="spinner" /> : (r?.reviews || []).length === 0 ? (
        <p style={{ color: 'var(--text-muted)', fontSize: 'var(--font-sm)' }}>No ratings yet. New buyers can see these once you have them.</p>
      ) : (r.reviews || []).map(rev => (
        <div key={rev.orderId} style={{ borderBottom: '1px solid var(--border)', padding: '8px 0' }}>
          <div style={{ color: '#f59e0b' }}>{'★'.repeat(rev.rating)}<span style={{ color: 'var(--border)' }}>{'★'.repeat(5 - rev.rating)}</span> <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{rev.buyerName} · {rev.productName}</span></div>
          {rev.feedback && <div style={{ fontSize: 13, marginTop: 2 }}>"{rev.feedback}"</div>}
        </div>
      ))}
    </div>
  );
}

// ── Coupons & Offers tab ──
function CouponsTab() {
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({ code: '', description: '', discountPct: 10, maxUses: 100, expiresAt: '', isFlashOffer: false, offerLabel: '' });
  const setF = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const { data, loading, refetch } = useQuery(MY_COUPONS_QUERY);
  const [createCoupon, { loading: cl }] = useMutation(CREATE_COUPON_MUTATION, {
    onCompleted: () => { refetch(); setCreating(false); setForm({ code: '', description: '', discountPct: 10, maxUses: 100, expiresAt: '', isFlashOffer: false, offerLabel: '' }); setError(''); },
    onError: e => setError(e.message),
  });
  const [deleteCoupon] = useMutation(DELETE_COUPON_MUTATION, { onCompleted: refetch });
  const [setFlash] = useMutation(SET_FLASH_OFFER_MUTATION, { onCompleted: refetch });
  const coupons = data?.myCoupons || [];
  const handleCreate = () => {
    setError('');
    if (!form.code.trim()) { setError('Coupon code is required'); return; }
    if (!form.description.trim()) { setError('Description is required'); return; }
    createCoupon({ variables: { code: form.code.toUpperCase().trim(), description: form.description, discountPct: parseInt(form.discountPct), maxUses: parseInt(form.maxUses), expiresAt: form.expiresAt || null, isFlashOffer: form.isFlashOffer, offerLabel: form.offerLabel || null } }).catch(() => {});
  };
  return (
    <div>
      <div className="card" style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <h2 style={{ fontWeight: 800, fontSize: 'var(--font-lg)' }}>🏷️ Coupons & Offers</h2>
          <button className="btn btn-ghost btn-sm" onClick={() => setCreating(v => !v)}>{creating ? '✕ Cancel' : '+ New'}</button>
        </div>
        {creating && (
          <div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
              <div className="form-group" style={{ margin: 0 }}><label className="form-label">Code *</label><input className="input" placeholder="TASTE1" value={form.code} onChange={e => setF('code', e.target.value.toUpperCase())} /></div>
              <div className="form-group" style={{ margin: 0 }}><label className="form-label">Discount %</label><input className="input" type="number" min={1} max={100} value={form.discountPct} onChange={e => setF('discountPct', e.target.value)} /></div>
            </div>
            <div className="form-group"><label className="form-label">Description *</label><input className="input" placeholder="Free taster on your first order" value={form.description} onChange={e => setF('description', e.target.value)} /></div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
              <div className="form-group" style={{ margin: 0 }}><label className="form-label">Max uses</label><input className="input" type="number" min={1} value={form.maxUses} onChange={e => setF('maxUses', e.target.value)} /></div>
              <div className="form-group" style={{ margin: 0 }}><label className="form-label">Expires (optional)</label><input className="input" type="date" value={form.expiresAt} onChange={e => setF('expiresAt', e.target.value)} /></div>
            </div>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 12, flexWrap: 'wrap' }}>
              <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 'var(--font-sm)' }}>
                <input type="checkbox" checked={form.isFlashOffer} onChange={e => setF('isFlashOffer', e.target.checked)} /> ⚡ Offer of the day (flash it at the top)
              </label>
              {form.isFlashOffer && <input className="input" style={{ width: 140, padding: '6px 10px' }} placeholder='Label e.g. "1+1"' value={form.offerLabel} onChange={e => setF('offerLabel', e.target.value)} />}
            </div>
            {error && <div style={{ color: 'var(--danger)', fontSize: 'var(--font-sm)', marginBottom: 10 }}>{error}</div>}
            <button className="btn btn-primary" style={{ width: '100%' }} onClick={handleCreate} disabled={cl}>{cl ? 'Creating…' : '🏷️ Create'}</button>
          </div>
        )}
      </div>
      <div className="card">
        {loading ? <div className="loading-center"><div className="spinner" /></div> : coupons.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 24, color: 'var(--text-muted)' }}>No coupons yet.</div>
        ) : (
          <div style={{ display: 'grid', gap: 10 }}>
            {coupons.map(c => (
              <div key={c.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 14px', background: 'var(--surface2)', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)', flexWrap: 'wrap', gap: 8 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <span style={{ fontFamily: 'monospace', fontWeight: 800, background: 'var(--primary-light)', color: 'var(--primary)', padding: '2px 10px', borderRadius: 6 }}>{c.code}</span>
                    <span style={{ fontWeight: 700, color: 'var(--success)', fontSize: 'var(--font-sm)' }}>{c.discountPct}% OFF</span>
                    {c.isFlashOffer && <span className="badge" style={{ background: 'rgba(236,72,153,0.15)', color: '#ec4899' }}>⚡ {c.offerLabel || 'Offer of the day'}</span>}
                    {!c.isActive && <span className="badge" style={{ background: 'rgba(107,114,128,0.15)', color: '#6b7280' }}>Inactive</span>}
                  </div>
                  <div style={{ fontSize: 'var(--font-sm)', color: 'var(--text-muted)', marginTop: 4 }}>{c.description} · {c.usedCount}/{c.maxUses} used</div>
                </div>
                <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  <button className="btn btn-ghost btn-sm" onClick={() => setFlash({ variables: { couponId: c.id, isFlashOffer: !c.isFlashOffer, offerLabel: c.offerLabel || (c.isFlashOffer ? '' : '1+1') } })}>
                    {c.isFlashOffer ? '✕ Unflash' : '⚡ Flash'}
                  </button>
                  <button className="btn btn-ghost btn-sm" style={{ color: 'var(--danger)' }} onClick={() => { if (window.confirm('Delete this coupon?')) deleteCoupon({ variables: { id: c.id } }); }}>🗑</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// Add/update the seller's UPI id and their own QR (buyers pay directly to either).
function UpiSettings() {
  const { currentUser, updateCurrentUser } = useAuth();
  const [upi, setUpi] = useState(currentUser?.upiId || '');
  const [qr, setQr] = useState(currentUser?.upiQrImage || '');
  const [saved, setSaved] = useState(false);
  const [err, setErr] = useState('');
  const [update, { loading }] = useMutation(UPDATE_PROFILE_MUTATION, {
    onCompleted: (d) => {
      updateCurrentUser({ upiId: d.updateProfile.upiId, upiQrImage: d.updateProfile.upiQrImage });
      setSaved(true); setErr(''); setTimeout(() => setSaved(false), 1500);
    },
    onError: (e) => setErr(e.message),
  });
  const active = currentUser?.upiId || currentUser?.upiQrImage;
  return (
    <div className="card" style={{ marginBottom: 20, background: 'var(--surface2)' }}>
      <div style={{ fontWeight: 700, marginBottom: 4 }}>📲 UPI payments {active ? <span className="badge badge-success" style={{ marginLeft: 6 }}>Active</span> : <span className="badge" style={{ marginLeft: 6, background: 'rgba(230,81,0,0.12)', color: 'var(--warning)' }}>Not set</span>}</div>
      <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 10 }}>Buyers can pay you directly by UPI. Orders paid by UPI arrive as “pending” for you to confirm.</div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 12 }}>
        <input className="input" style={{ flex: 1, minWidth: 200 }} placeholder="yourname@okhdfcbank" value={upi} onChange={e => { setUpi(e.target.value); setErr(''); }} />
      </div>
      {/* Uploading the QR from your own UPI app is the route that actually completes: buyers' apps
          accept their own collect code, while a link built from a UPI id is often refused. */}
      <UpiQrPicker
        value={qr}
        onChange={v => { setQr(v); setErr(''); }}
        vpa={upi}
        onVpaFound={v => setUpi(v)}
        hint="Open your UPI app → Receive money / My QR → screenshot it, crop to the code, and upload. We'll read your UPI id out of it so buyers can tap to pay."
      />
      <button className="btn btn-primary btn-sm" disabled={loading}
        onClick={() => update({ variables: { upiId: upi.trim(), upiQrImage: qr } })}>
        {loading ? 'Saving…' : saved ? '✅ Saved' : 'Save UPI details'}
      </button>
      {err && <div style={{ color: 'var(--danger)', fontSize: 'var(--font-sm)', marginTop: 8 }}>{err}</div>}
    </div>
  );
}

export default function SellerDashboard() {
  const { currentUser } = useAuth();
  const [tab, setTab] = useState('orders');
  const isSeller = currentUser?.role === 'SELLER' || currentUser?.role === 'ADMIN';
  const { data, loading } = useQuery(SELLER_DASHBOARD_QUERY, { skip: !isSeller, fetchPolicy: 'cache-and-network' });

  if (!currentUser) return <div className="page"><div className="container" style={{ paddingTop: 100, textAlign: 'center' }}>Please sign in.</div></div>;
  if (!isSeller) return <SellerRegister />;

  const acct = data?.mySellerAccount;
  const orders = data?.sellerOrders || [];
  const customers = data?.sellerCustomers || [];
  const currency = currentUser.currency;

  return (
    <div className="page">
      <div className="container" style={{ padding: '32px 16px', maxWidth: 860 }}>
        <div style={{ marginBottom: 20 }}>
          <h1 style={{ fontSize: 'var(--font-xl)', fontWeight: 800, marginBottom: 4 }}>🛍️ Seller Dashboard</h1>
          <p style={{ color: 'var(--text-muted)', fontSize: 'var(--font-sm)' }}>{currentUser.storePersona ? personaLabel(currentUser.storePersona) + ' · ' : ''}Orders, customer accounts, P&L, offers & ratings.</p>
        </div>

        <UpiSettings />

        {/* Summary stats */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 12, marginBottom: 20 }}>
          <div className="card" style={{ textAlign: 'center', padding: '16px 12px' }}><div style={{ fontSize: 24 }}>📦</div><div style={{ fontSize: 24, fontWeight: 800, color: 'var(--primary)' }}>{acct?.orderCount ?? 0}</div><div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Orders</div></div>
          <div className="card" style={{ textAlign: 'center', padding: '16px 12px' }}><div style={{ fontSize: 24 }}>👥</div><div style={{ fontSize: 24, fontWeight: 800, color: 'var(--warning)' }}>{acct?.customerCount ?? 0}</div><div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Customers</div></div>
          <div className="card" style={{ textAlign: 'center', padding: '16px 12px' }}><div style={{ fontSize: 24 }}>💷</div><div style={{ fontSize: 24, fontWeight: 800, color: 'var(--success)' }}>{money(acct?.totalAmount ?? 0, currency)}</div><div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Collected</div></div>
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', gap: 6, marginBottom: 16, flexWrap: 'wrap' }}>
          {TABS.map(t => (
            <button key={t.id} className={`btn btn-sm ${tab === t.id ? 'btn-primary' : 'btn-ghost'}`} onClick={() => setTab(t.id)}>{t.label}</button>
          ))}
        </div>

        {loading && !data ? <div className="loading-center"><div className="spinner" /></div> : (
          <>
            {tab === 'orders' && <OrdersTab orders={orders} currency={currency} />}
            {tab === 'customers' && <CustomersTab customers={customers} currency={currency} />}
            {tab === 'pnl' && <PnLTab orders={orders} currency={currency} />}
            {tab === 'coupons' && <CouponsTab />}
            {tab === 'ratings' && <RatingsTab sellerId={currentUser.id} />}
          </>
        )}
      </div>
    </div>
  );
}
